import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import client from "@/lib/api";
import { SEARCH_CATS } from "@/components/GlobalSearch";
import { Search, Loader2, ArrowRight } from "lucide-react";

const CAT_ORDER = ["clients", "properties", "sites", "staff", "jobs", "issues", "messages"];

export default function SearchResults() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const run = useCallback(async (query) => {
    if (query.trim().length < 2) { setGroups({}); return; }
    setLoading(true);
    try {
      const { data } = await client.get("/search", { params: { q: query, limit: 25 } });
      setGroups(data.groups || {});
    } catch {
      setGroups({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setInput(q); run(q); }, [q, run]);

  const submit = (e) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const cats = CAT_ORDER.filter((c) => groups[c]?.length);
  const total = cats.reduce((n, c) => n + groups[c].length, 0);
  const visible = filter === "all" ? cats : cats.filter((c) => c === filter);

  return (
    <div className="space-y-6" data-testid="search-results-page">
      <div>
        <h1 className="font-display font-bold text-slate-900 text-2xl">{t("search.title", "Search")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("search.emptyState", "Search across records you can access.")}</p>
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 h-11 px-4 rounded-full bg-white border border-slate-200 focus-within:border-brand/40 focus-within:ring-2 focus-within:ring-brand/15 max-w-2xl transition">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          data-testid="search-page-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("search.placeholder", "Search iClean…")}
          className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
          autoFocus
        />
        {loading && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
      </form>

      {q.trim().length >= 2 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="search-filters">
          <button
            onClick={() => setFilter("all")}
            data-testid="search-filter-all"
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {t("search.all", "All")} · {total}
          </button>
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`search-filter-${c}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === c ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {t(`search.cat.${c}`, c)} · {groups[c].length}
            </button>
          ))}
        </div>
      )}

      {q.trim().length < 2 ? (
        <p className="text-sm text-slate-400 py-8" data-testid="search-page-hint">{t("search.hint", "Type at least 2 characters…")}</p>
      ) : loading ? (
        <p className="text-sm text-slate-400 py-8 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("search.searching", "Searching…")}</p>
      ) : total === 0 ? (
        <p className="text-sm text-slate-400 py-8" data-testid="search-page-empty">{t("search.noResults", "No results found.")}</p>
      ) : (
        <div className="space-y-6">
          {visible.map((cat) => {
            const Icon = SEARCH_CATS[cat].icon;
            return (
              <div key={cat} data-testid={`search-section-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-8 w-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center">
                    <Icon className="h-4 w-4" />
                  </div>
                  <h2 className="font-semibold text-slate-900">{t(`search.cat.${cat}`, cat)}</h2>
                  <span className="text-xs text-slate-400">· {groups[cat].length} {t("search.count", "results")}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groups[cat].map((r) => (
                    <button
                      key={`${cat}-${r.id}`}
                      data-testid={`search-card-${cat}-${r.id}`}
                      onClick={() => navigate(cat === "jobs" ? `/jobs/${r.id}` : SEARCH_CATS[cat].route)}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-brand/40 hover:shadow-sm transition group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{r.title || "—"}</p>
                          {r.sub && <p className="text-xs text-slate-400 truncate mt-0.5">{r.sub}</p>}
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-brand transition flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
